import { computed, inject, Injector, Type } from '@angular/core';
import {
  DOCUMENTS_V2_URL,
  SDInitialState,
  SUBMITTED_DOCUMENT_GRID_STATE,
} from '@data-access/documents/contants';
import { DocumentsService } from '@data-access/documents/services';
import {
  DocumentTaskRaw,
  DocumentTypeExtended,
  EditDocument,
  RequestDocument,
  SubmittedDocument,
  SubmittedDocumentsSignalState,
  UpdateBulkDocument,
  UpdateDocument,
} from '@data-access/documents/types';
import {
  applyAdvancedFilters,
  applyPagination,
  applySearch,
  applySort,
  constructGridFilters,
  DEFAULT_CHECKBOX_PROPS,
  DIALOG_TYPE,
  LoadGridPayload,
  OpenDialogState,
  transformGridDateFormat,
  withGridConfigFeatures,
} from '@data-access/shared';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { ShallowCamelCasePipe } from '@shared/pipes/shallow-camel-case.pipe';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  BuildLinkFromValue,
  GridColumn,
  GridColumnCasePipe,
} from '@wcg/frontend-framework';
import { withSubmittedDocumentsEntities } from './entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { map, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { SubmissionResponse } from '@data-access/submissions';
import { withDocumentTypeEntities } from '../features/with-document-types.entities';
import { withGridCheckBoxFeature } from '@data-access/shared/store/features/with-grid-checkbox-feature.store';
import { documentFeatureMethods } from './util';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogComponent } from '@shared/dialogs/components/delete-dialog/delete-dialog.component';
import { getDeleteDialogData } from '../../utils';
import { cloneDeep } from 'lodash';

export const SUBMITED_DOCUMENTS_SIGNAL_STORE = signalStore(
  withState(() => inject(SUBMITTED_DOCUMENT_GRID_STATE)),
  withProps((store, _documentService = inject(DocumentsService)) => ({
    _buildLinkFromValue: new BuildLinkFromValue(),
    _documentService,
    _gridCasePipe: new GridColumnCasePipe(),
    _injector: inject(Injector),
    _matDialog: inject(MatDialog),
    _shallowCamelCasePipe: new ShallowCamelCasePipe(),
    _wcgTableUtilService: inject(WcgTableUtilsService),
    store,
  })),
  withFeature(({ _injector }) => withDocumentTypeEntities(_injector)),
  withFeature(documentFeatureMethods),
  withFeature(() => withGridConfigFeatures()),
  withFeature(() => withGridCheckBoxFeature()),
  withFeature(() => withSubmittedDocumentsEntities()),
  withComputed(
    ({
      error,
      _submittedDocumentsEntities,
      selectedView,
      total,
      isLoading,
      currentUrl,
      gridType,
      workspaceId,
      currentPageDocuments,
      _documentTypesEntities,
      store,
      _injector,
    }) => ({
      $currentUrl: computed(() => currentUrl()),
      $documents: computed(() => {
        const _SDocuments = _submittedDocumentsEntities();
        const _PDocuments = store.filteredByTask()
          ? []
          : store.pendingDocuments();
        if (!_SDocuments && !_PDocuments) {
          return [] as SubmittedDocument[];
        }

        return transformGridDateFormat<SubmittedDocument>(
          _injector,
          [..._PDocuments, ..._SDocuments],
          store.columnsCopy(),
        );
      }),
      $currentPageDocuments: computed(() => currentPageDocuments()),
      $error: computed(() => error()),
      $gridType: computed(() => gridType()),
      $isLoading: computed(() => isLoading()),
      $selectedView: computed(() => selectedView()),
      $totalCount: computed(() => total()),
      $workspaceId: computed(() => workspaceId()),
      $documentTypes: computed(() => {
        const _documentTypes = _documentTypesEntities();
        if (!_documentTypes) {
          return [] as DocumentTypeExtended[];
        }
        return _documentTypes;
      }),
      $documentTasks: computed(() => store.documentTasks()),
      $isAllSelected: computed(() => store.isAllSelected()),
      $isIndeterminate: computed(() => store.isIndeterminate()),
      $selectedRows: computed(() => store.selectedRows()),
      $taskInstanceId: computed(() => store.taskInstanceId()),
      $uploadedRequiredTaskIds: computed(() => store.uploadedRequiredTaskIds()),
      $navigateToRequiredDocuments: computed(() =>
        store.navigateToRequiredDocuments(),
      ),
    }),
  ),
  withMethods(
    ({
      _documentService,
      _shallowCamelCasePipe,
      _wcgTableUtilService,
      _buildLinkFromValue,
      _matDialog,
      _injector,
      ...store
    }) => {
      const _loadWorkspaceDocumentTasks = rxMethod<string>(
        pipe(
          switchMap((workspaceId) =>
            _documentService.getWorkspaceDocumentTasks(workspaceId).pipe(
              map((response) => {
                return response.data as unknown as DocumentTaskRaw[];
              }),
              tapResponse({
                error: () => patchState(store, { documentTasks: [] }),
                next: (documentTasks: DocumentTaskRaw[]) => {
                  patchState(store, { documentTasks });
                },
              }),
            ),
          ),
        ),
      );

      const _loadCurrentPageDocuments = (
        loadGridPayload: LoadGridPayload,
      ): void => {
        const params = _wcgTableUtilService.convertHttpParamsToObject(
          loadGridPayload.params,
        );

        const documents = store.$documents() ?? [];
        const columns = Object.values(store.$columns()).map(
          (col: GridColumn) => _shallowCamelCasePipe.transform(col).propKey,
        );

        let processedDocs = applySearch(documents, columns, params.query ?? '');

        if (params.filters.length) {
          const filters = JSON.parse(atob(params.filters));
          processedDocs = applyAdvancedFilters(
            processedDocs,
            filters,
            _injector,
          );
        }
        processedDocs = applySort(
          processedDocs,
          params.sort_by_field as keyof SubmittedDocument,
          params.sort_by_order,
        );

        const paginatedDocs = applyPagination(
          processedDocs,
          params.page,
          params.page_size,
        );

        let _navigateToRequiredDocuments = false;
        if (paginatedDocs.length === 0 && params.page.toString() !== '1') {
          _reloadDocuments(true);
        } else if (
          paginatedDocs.length === 0 &&
          store.filteredByTask() &&
          params.filters.length === 0 &&
          (params.query === '' || !params.query)
        ) {
          _navigateToRequiredDocuments = true;
        }

        patchState(store, {
          currentStateDestroyed: false,
          total: processedDocs.length,
          currentPageDocuments: paginatedDocs,
          navigateToRequiredDocuments: _navigateToRequiredDocuments,
        });
      };

      const _reloadDocuments = (resetPageIndex = false) => {
        if (resetPageIndex) {
          patchState(store, {
            pageView: { page: 1, pageSize: store.$pageView().pageSize },
          });
        }
        patchState(store, {
          isLoading: false,
          canCloseDialog: true,
          selectedRows: [],
          isAllSelected: false,
          isIndeterminate: false,
        });
        let _params = _wcgTableUtilService.generateTableQueryParams({
          filters: constructGridFilters(store.$advancedFilters()),
          page: store.$pageView().page,
          pageSize: store.$pageView().pageSize,
          searchValue: store.$searchString(),
          sortParam: {
            dir: store.$sort()?.sortByOrder || 'asc',
            field: store.$sort()?.sortByField || '',
          },
        });
        const taskId = store.taskInstanceId();
        if (store.filteredByTask() && taskId) {
          _params = _params.set('task_instance_id', taskId);
        }
        const _payload: LoadGridPayload = {
          params: _params,
        };
        _loadSDocuments(_payload);
      };

      const _loadSDocuments = rxMethod<LoadGridPayload>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ params, updateColumns }) => {
            const currentWorkspaceId = store.workspaceId();
            if (!currentWorkspaceId) {
              console.error('No workspaceId set');
              return [];
            }
            _loadWorkspaceDocumentTasks(currentWorkspaceId);
            return _documentService
              .getDocumentsByEntity(currentWorkspaceId, 'workspace', params)
              .pipe(
                tap(() =>
                  patchState(store, {
                    error: null,
                    isLoading: true,
                  }),
                ),
                map((response) => ({
                  ...response,
                  config: _shallowCamelCasePipe.transform(response.config),
                })),
                tapResponse({
                  error: (error: Error) => patchState(store, { error: error }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response) => {
                    const _uploadedRequiredTaskIds = cloneDeep(
                      response.data,
                    ).flatMap((item) =>
                      item.document_tasks.map((task) => task.task_instance_id),
                    );
                    patchState(store, {
                      currentStateDestroyed: false,
                      currentUrl: `${DOCUMENTS_V2_URL}workspace/${currentWorkspaceId}/`,
                      filteredByTask: params.has('task_instance_id'),
                      taskInstanceId: params.get('task_instance_id') || null,
                      uploadedRequiredTaskIds: _uploadedRequiredTaskIds,
                    });
                    const data =
                      Array.isArray(response.data) && response.data.length > 0
                        ? response.data.map((doc) => ({
                            ...doc,
                            complete_timestamp: doc.is_complete
                              ? doc.complete_timestamp
                              : null,
                          }))
                        : [];
                    store.loadSubmittedDocuments(data);
                    // Force advanced filters update, until we figure out the
                    // impact we can leave this in. But ideally we
                    // fetch/update advanced filters no matter what our
                    // response (dataObj) is
                    // Scenario this was encountered:
                    // Fetching documents gives no results, but we still want
                    // to fetch the document type dropdown

                    store.loadConfigurations(
                      response.config,
                      updateColumns,
                      data?.[0],
                      _buildLinkFromValue,
                    );
                    store.setGridViews(response.views);
                    store.setAllRows(store.selectedRows(), data);

                    _loadCurrentPageDocuments({
                      params,
                      updateColumns,
                    });
                  },
                }),
              );
          }),
        ),
      );

      const _openDocumentDialog = (dialogMetaData: OpenDialogState) => {
        store.openDialog({
          component: () => dialogMetaData.component(),
          componentData: dialogMetaData.componentData,
          type: DIALOG_TYPE.GroupFields,
        });
      };

      const _updateDocument = rxMethod<{
        document: EditDocument;
        submission: Partial<SubmissionResponse>;
      }>(
        pipe(
          tap(() => patchState(store, { error: null, isLoading: true })),
          switchMap(({ document, submission }) => {
            /** Task IDs cannot be 'none' or undefined when updating a document */
            const _document: UpdateDocument = {
              ...(document.document_type?.id && {
                document_type_id: document.document_type.id,
              }),
              task_ids:
                (document.tasks || [])
                  .map((task) => task.id)
                  .filter((id): id is string => !!id && id !== 'none') || [],
            };

            return _documentService
              .updateDocument(document.id, _document, submission)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error,
                      isLoading: false,
                    }),
                  next: (data) => {
                    const _doc = store
                      .$documents()
                      .find((doc) => doc.id === data.id);
                    if (_doc) {
                      patchState(store, {
                        isLoading: false,
                        canCloseDialog: true,
                        selectedRows: [],
                        isAllSelected: false,
                        isIndeterminate: false,
                      });
                      _reloadDocuments();
                    }
                  },
                }),
              );
          }),
        ),
      );

      const _updateBulkDocuments = rxMethod<{
        document: EditDocument;
        submission: Partial<SubmissionResponse>;
      }>(
        pipe(
          tap(() => patchState(store, { error: null, isLoading: true })),
          switchMap(({ document, submission }) => {
            /** Task IDs cannot be 'none' or undefined when updating a document */
            const _bulkDocument: UpdateBulkDocument = {
              document_ids: document.document_ids || [],
              task_ids:
                (document.tasks || [])
                  .map((task) => task.id)
                  .filter((id): id is string => !!id && id !== 'none') || [],
              ...(document.document_type?.id && {
                document_type_id: document.document_type.id,
              }),
              workspace_id: store.workspaceId() || '',
            };

            return _documentService
              .updateBulkDocuments(_bulkDocument, submission)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error,
                      isLoading: false,
                    }),
                  next: (data) => {
                    const _data = data as { updated_ids: string[] };
                    if (_data.updated_ids.length > 0) {
                      patchState(store, {
                        canCloseDialog: true,
                        isLoading: false,
                        ...DEFAULT_CHECKBOX_PROPS,
                      });
                      _reloadDocuments();
                    }
                  },
                }),
              );
          }),
        ),
      );

      const _requestDocument = rxMethod<RequestDocument>(
        pipe(
          tap(() => patchState(store, { error: null, isLoading: true })),
          switchMap((payload) => {
            return _documentService.requestDocument(payload).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, {
                    error,
                    isLoading: false,
                  }),
                next: () => {
                  patchState(store, {
                    canCloseDialog: true,
                    isLoading: false,
                  });
                },
              }),
            );
          }),
        ),
      );

      const _confirmAndDeleteDocuments = (
        documentIds: string,
        currentStage: string,
      ) => {
        const documentIdArray = documentIds.split(',').filter(Boolean);
        const count = documentIdArray.length;
        const dialogData = getDeleteDialogData(count);
        const dialogRef = _matDialog.open(DeleteDialogComponent, {
          data: dialogData,
          width: '640px',
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) {
            if (count > 1) {
              patchState(store, { isLoading: true });
              store.bulkDeleteDocument(
                store.workspaceId() || '',
                currentStage,
                documentIds,
                () => {
                  _reloadDocuments();
                },
              );
            } else {
              store.deleteDocument(
                store.workspaceId() || '',
                currentStage,
                documentIds,
                () => {
                  _reloadDocuments();
                },
              );
            }
          }
        });
      };

      return {
        deleteDocument: (documentId: string, currentStage: string) => {
          _confirmAndDeleteDocuments(documentId, currentStage);
        },

        bulkDeleteDocument: (documentIds: string, currentStage: string) => {
          _confirmAndDeleteDocuments(documentIds, currentStage);
        },
        downloadSingleDocument: (documentId: string) =>
          store.downloadDocument(documentId),
        openDocumentInNewTab: (documentId: string) =>
          store.openInNewTab(documentId),
        loadSDGrid: (params: LoadGridPayload) => _loadSDocuments(params),
        loadCurrentPageDocuments: (loadGridPayload: LoadGridPayload) => {
          if (Object.keys(store.$advancedFilters()).length) {
            _loadCurrentPageDocuments(loadGridPayload);
          } else {
            _reloadDocuments(true);
          }
        },
        resetState: () =>
          patchState(store, {
            ...SDInitialState,
            currentStateDestroyed: true,
            error: null,
            isLoading: false,
          }),
        resetGridSettings: (payload: LoadGridPayload) => {
          patchState(store, { searchString: '', ...DEFAULT_CHECKBOX_PROPS });
          store.setDefaultGridView();
          const taskInstanceId = store.taskInstanceId();
          let _payload = {
            ...payload,
            params: payload.params.set('filter_by', 'all'),
          };
          if (taskInstanceId) {
            _payload = {
              ...payload,
              params: payload.params.set('task_instance_id', taskInstanceId),
            };
          }

          _loadSDocuments(_payload);
        },
        setWorkspaceId: (workspaceId: string) =>
          patchState(store, { workspaceId, documentTasks: [] }),
        setWorkspaceIntId: (workspaceIntId: string) =>
          patchState(store, { workspaceIntId }),
        loadDocumentTypes: (params: { workspaceId?: string }) => {
          patchState(store, {
            ...DEFAULT_CHECKBOX_PROPS,
          });
          store.setDocumentTypes(params);
        },
        openEditDocumentDialog: (dialogMetaData: OpenDialogState) => {
          patchState(store, { canCloseDialog: false });
          _openDocumentDialog(dialogMetaData);
        },
        updateDocument: (data: {
          document: EditDocument;
          submission: Partial<SubmissionResponse>;
        }) => {
          if ((data.document.document_ids ?? []).length > 1) {
            _updateBulkDocuments(data);
          } else {
            _updateDocument(data);
          }
        },

        selectAll: (checked: boolean) =>
          store.selectAll(store.selectedRows(), store.allRows(), checked),

        selectRow: (row: SubmittedDocument, checked: boolean) =>
          store.selectRow(store.selectedRows(), store.allRows(), row, checked),

        resetSelectedRows: () =>
          patchState(store, {
            isAllSelected: false,
            isIndeterminate: false,
            selectedRows: [],
          }),

        addPendingDocuments: (documents: SubmittedDocument[]) => {
          patchState(store, {
            pendingDocuments: documents,
          });
          _loadCurrentPageDocuments({
            params: _wcgTableUtilService.generateTableQueryParams({
              filters: constructGridFilters(store.$advancedFilters()),
              page: store.$pageView().page,
              pageSize: store.$pageView().pageSize,
              searchValue: store.$searchString(),
              sortParam: {
                dir: store.$sort()?.sortByOrder || 'asc',
                field: store.$sort()?.sortByField || '',
              },
            }),
            updateColumns: false,
          });
        },

        openRequestDocumentDialog: (dialogMetaData: OpenDialogState) => {
          patchState(store, { canCloseDialog: false, isLoading: false });
          _openDocumentDialog(dialogMetaData);
        },
        requestDocument: (document: RequestDocument) =>
          _requestDocument(document),
      };
    },
  ),
) satisfies Type<SubmittedDocumentsSignalState>;
